import React, { useState } from 'react';
import styled from 'styled-components';
import { KeyboardDetectionProvider, makeColorSetClass, screenSmMax, spacer16, spacer24, cssColorValue, spacer32, spacer56, spacer8, screenLgMax, screenMdMax, Type, IconPause, IconPlay, ButtonSecondary, spacer72, desktopAlto, desktopBallad, desktopBass, desktopCanon, desktopForte, desktopBrio, mobileBrio, } from '@spotify-internal/encore-web';
import { DELAYS, ImageAspectBox1x1, fadeInAnimation, Grid, withAnimationContext, BackgroundVideo, TWO_COLUMN_MAX_WIDTH, } from '../../../ui';
import { JumpLinkNav } from './JumpLinkNav';
import { formatText } from '../../../ui/utils';
import { ColorSetWrapper } from '../../../ui/Colors/Colors';
import { Media } from '../../../ui/Components/Media';
import { useForegroundPauseRef } from '../../../ui/Components/VideoPlayer/hooks/useForegroundPauseRef';
const screenLgMid = '1439px';
const Container = styled.div `
  position: relative;
  .knockout-text {
    color: black;
    fill: black;
    mix-blend-mode: screen;
    .under {
      color: ${({ textColor }) => textColor} !important;
      fill: ${({ textColor }) => textColor} !important;
      background: transparent;
      // knockout on small screens
      @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
        color: black !important;
        fill: black !important;
        background: ${({ textColor }) => textColor} !important;
      }
    }
    .mask {
      background: ${({ textColor }) => textColor} !important;
    }
  }
`;
const JumpLinkHero = styled(Grid) `
  grid-template-rows: auto 1fr;
  color: ${cssColorValue('textBase')};
  background: ${cssColorValue('backgroundBase')};
  // add extra top padding for nav when header
  padding-top: ${({ isHeader }) => isHeader ? `calc(${spacer56} + ${spacer72})` : spacer56};
  padding-bottom: ${spacer32};
  height: auto;

  @media (max-width: ${screenSmMax}) {
    // add extra top padding for nav when header
    padding-top: ${({ isHeader }) => isHeader ? `calc(${spacer24} + ${spacer56})` : spacer24};
    padding-bottom: ${spacer8};
  }

  // override spbootstrap css
  // it is setting border-box and that messes with jumplink/headline height detection
  .jl,
  .jl-headline {
    box-sizing: unset !important;
  }
`;
const NoMediaPlaceholder = styled(ImageAspectBox1x1) `
  width: 100%;

  @media (max-width: ${screenSmMax}) {
    grid-column: 1 / -1;
  }
`;
const HorizontalDiv = styled.div `
  grid-column: 1 / -1;
  padding-top: 16px;
  padding-bottom: 32px;
  background: ${cssColorValue('backgroundBase')};
  color: ${cssColorValue('textBase')};
`;
const Headline = styled(Type) `
  padding-bottom: 0px;
  line-height: normal;
  ${desktopBass}

  @media (max-width: ${screenLgMax}) {
    ${({ useLargeText }) => (useLargeText ? desktopBass : desktopForte)}
  }

  @media (max-width: ${screenLgMid}) {
    ${desktopForte}
  }

  @media (max-width: ${screenMdMax}) {
    ${desktopBrio}
  }

  @media (max-width: ${screenSmMax}) {
    ${mobileBrio}
    text-align: left;
  }
`;
const Tagline = styled(Type) `
  padding-bottom: 0px;
  padding-top: 16px;
  ${desktopBrio}

  @media (max-width: ${screenLgMax}) {
    ${({ useLargeText }) => (useLargeText ? desktopBrio : desktopAlto)}
  }

  @media (max-width: ${screenLgMid}) {
    ${desktopAlto}
  }

  @media (max-width: ${screenMdMax}) {
    ${desktopCanon}
  }
`;
const Paragraph = styled(Type) `
  padding-bottom: 0px;
  padding-top: 20px;
  font-size: 24px;
  line-height: 32px;
  font-weight: 450;
  --webkit-font-smoothing: antialiased;

  @media (max-width: ${screenLgMax}) {
    font-size: 20px;
    line-height: 28px;
  }

  @media (max-width: ${screenLgMid}) {
    ${desktopBallad}
    line-height: normal;
  }
`;
const MediaBox = styled.div `
  grid-row: 1 / 3;
  grid-column: ${({ layout }) => (layout === 'left' ? '1 / 5' : '9 / 13')};
  order: ${({ layout }) => (layout === 'left' ? '1' : '2')};
  align-self: center;
  @media (max-width: ${screenSmMax}) {
    grid-column: 1 / -1;
    order: 1;
  }
`;
const TextFlexWrapper = styled.div `
  display: flex;
  height: 100%;
  align-items: center;
  grid-column: 1 / 9;
  grid-row: 1/3;
  @media (max-width: ${screenSmMax}) {
    grid-column: 1 / -1;
    grid-row: unset;
    order: 2;
  }
`;
const TextSubgrid = styled.div `
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  column-gap: ${spacer24};
  z-index: 5;
  @media (max-width: ${screenSmMax}) {
    padding-top: ${spacer32};
  }
`;
const JumpLinkEyebrow = withAnimationContext(styled(Type) `
  ${fadeInAnimation(DELAYS.FIRST)};
  grid-column: 1 / 8;
  padding-bottom: ${spacer16};
  @media (max-width: ${screenSmMax}) {
    grid-column: 1 / -1;
  }
`);
const JumpLinkHeadline = withAnimationContext(styled(Headline) `
  ${({ hasEyebrow }) => hasEyebrow ? fadeInAnimation(DELAYS.SECOND) : fadeInAnimation(DELAYS.FIRST)}
  grid-column: 1 / 8;
  @media (max-width: ${screenSmMax}) {
    grid-column: 1 / -1;
  }
`);
const JumpLinkP = withAnimationContext(styled(Paragraph) `
  grid-column: 1 / 6;
  ${({ hasEyebrow }) => hasEyebrow ? fadeInAnimation(DELAYS.THIRD) : fadeInAnimation(DELAYS.SECOND)}
  @media (max-width: ${screenLgMax}) {
    grid-column: 1 / 7;
  }
  @media (max-width: ${screenMdMax}) {
    grid-column: 1 / 8;
  }
  @media (max-width: ${screenSmMax}) {
    grid-column: 1 / -1;
  }
`);
const JumpLinkTagline = withAnimationContext(styled(Tagline) `
  grid-column: 1 / 7;
  ${({ hasEyebrow }) => hasEyebrow ? fadeInAnimation(DELAYS.THIRD) : fadeInAnimation(DELAYS.SECOND)}
  @media (max-width: ${screenSmMax}) {
    grid-column: 1 / -1;
  }
`);
const JumpLinkNavDiv = styled.div ``;
const JumpLinkNavWrapper = withAnimationContext(styled(JumpLinkNavDiv) `
  ${({ hasEyebrow }) => hasEyebrow ? fadeInAnimation(DELAYS.FOURTH) : fadeInAnimation(DELAYS.THIRD)}
`);
const VideoButtonWrapper = withAnimationContext(styled.div `
  ${fadeInAnimation(DELAYS.FOURTH)};
  grid-column: 1 / -1;
  display: flex;
  width: 100%;
  justify-content: flex-end;
`);
export const JumpLinkHeroModule = React.forwardRef(({ isHeader = false, eyebrow, headline = '', isTagline = false, tagline = '', paragraph = '', image, media, layout = 'right', jumpLinks = [], moduleHoverColors, modulePrimaryColors, colors, eyebrowOrHeadline = false, useNumberedJumpLinks = false, useLargeText = false, useBackgroundVideo, backgroundVideoDesktop, backgroundVideoMobile, }, ref) => {
    const useColors = (colors === null || colors === void 0 ? void 0 : colors.backgroundColor) && (colors === null || colors === void 0 ? void 0 : colors.textColor);
    const [isBackgroundVideoPlaying, setIsBackgroundVideoPlaying] = useState(useBackgroundVideo ? true : false);
    const foregroundRef = useForegroundPauseRef({
        isPlaying: isBackgroundVideoPlaying,
        play: () => setIsBackgroundVideoPlaying(true),
        pause: () => setIsBackgroundVideoPlaying(false),
    });
    const colorsToUse = useBackgroundVideo
        ? {
            backgroundColor: colors === null || colors === void 0 ? void 0 : colors.backgroundColor,
            textColor: colors === null || colors === void 0 ? void 0 : colors.textColor,
            accentColor: colors === null || colors === void 0 ? void 0 : colors.textColor,
        }
        : colors;
    return (React.createElement(KeyboardDetectionProvider, null,
        React.createElement(ColorSetWrapper, { colors: colorsToUse },
            React.createElement("div", { ref: ref },
                React.createElement(Container, { textColor: colorsToUse === null || colorsToUse === void 0 ? void 0 : colorsToUse.textColor, ref: foregroundRef },
                    useBackgroundVideo && backgroundVideoDesktop && (React.createElement(BackgroundVideo, { desktopVideo: backgroundVideoDesktop, mobileVideo: backgroundVideoMobile, isPlaying: isBackgroundVideoPlaying })),
                    React.createElement(JumpLinkHero, { className: !useColors
                            ? makeColorSetClass((modulePrimaryColors === null || modulePrimaryColors === void 0 ? void 0 : modulePrimaryColors.theme) === 'light'
                                ? 'invertedLight'
                                : 'invertedDark')
                            : '', isHeader: isHeader },
                        React.createElement(MediaBox, { layout: layout }, useBackgroundVideo ? (React.createElement(NoMediaPlaceholder, null)) : (React.createElement(Media, { media: media, image: image }))),
                        React.createElement(TextFlexWrapper, null,
                            React.createElement(TextSubgrid, null,
                                eyebrow && (React.createElement(JumpLinkEyebrow, { variant: "ballad" }, eyebrow.toUpperCase())),
                                headline && (React.createElement(JumpLinkHeadline, { ref: ref, "data-testid": "hero-body-headline", useLargeText: useLargeText, hasEyebrow: eyebrow, dangerouslySetInnerHTML: {
                                        __html: formatText(headline),
                                    } })),
                                isTagline ? (React.createElement(JumpLinkTagline, { className: "tagline", useLargeText: useLargeText, hasEyebrow: eyebrow }, tagline)) : (React.createElement(JumpLinkP, { ref: ref, useLargeText: useLargeText, color: (colorsToUse === null || colorsToUse === void 0 ? void 0 : colorsToUse.accentColor) ||
                                        (modulePrimaryColors === null || modulePrimaryColors === void 0 ? void 0 : modulePrimaryColors.iconColor), className: "paragraph", hasEyebrow: eyebrow }, formatText(paragraph)))))),
                    React.createElement(Grid, { className: !useColors
                            ? makeColorSetClass((modulePrimaryColors === null || modulePrimaryColors === void 0 ? void 0 : modulePrimaryColors.theme) === 'light'
                                ? 'invertedLight'
                                : 'invertedDark')
                            : '' },
                        useBackgroundVideo && (React.createElement(VideoButtonWrapper, null,
                            React.createElement(ButtonSecondary, { iconOnly: isBackgroundVideoPlaying ? IconPause : IconPlay, buttonSize: "sm", style: { borderColor: cssColorValue('textBase') }, onClick: () => setIsBackgroundVideoPlaying(!isBackgroundVideoPlaying), "aria-label": isBackgroundVideoPlaying
                                    ? 'pause button'
                                    : 'play button' }))),
                        React.createElement(HorizontalDiv, null,
                            React.createElement(JumpLinkNavWrapper, { hasEyebrow: eyebrow, className: useBackgroundVideo ? 'knockout-text' : '' },
                                React.createElement(JumpLinkNav, { jumpLinks: jumpLinks, backgroundColor: cssColorValue('backgroundBase'), textColor: cssColorValue('textBase'), hoverBackground: (colorsToUse === null || colorsToUse === void 0 ? void 0 : colorsToUse.accentColor) ||
                                        (moduleHoverColors === null || moduleHoverColors === void 0 ? void 0 : moduleHoverColors.iconColor) ||
                                        '#0000FF', useNumberedJumpLinks: useNumberedJumpLinks, eyebrowOrHeadline: eyebrowOrHeadline })))))))));
});
//# sourceMappingURL=index.js.map